import {
  CssCustomPropertyGroupStyleItem,
  Styles,
  StylesDerivedNativeActionNames,
  StylesGroup,
} from '../../../types';
import { CompRef } from '@wix/document-services-types';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';
import {
  CssCustomProperties,
  CssProperties,
  DisplayGroups,
  DisplayGroupItem,
  DISPLAY_GROUPS,
} from '@wix/component-protocol/schema';
import { PROPERTY_SIBLINGS_MAP } from '../constants';
import { groupByDuplication } from './duplicateBasedGrouping';
import { STYLE_NAMESPACE } from '../../../../../apis/services/editActionsServices/nativeActions/globalDesign/types';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const getWidgetDisplayName = (context: VirtualManifestAPI): string => {
  return context.resolver.getDisplayName();
};

const processDefaultCustomPropertiesDuplicateGroups = (
  editorPointer: EditorPointer<CompRef>,
  allowedStyles: CssCustomProperties,
  predefinedGroups: Omit<StylesGroup, 'engine'>[],
  context: VirtualManifestAPI,
): Omit<StylesGroup, 'engine'>[] => {
  const styleItemsFromPredefinedGroups = new Set(
    predefinedGroups.flatMap((group) =>
      group.items.map((item) => item.styleItemId),
    ),
  );

  const filteredAllowedStyles = Object.fromEntries(
    Object.entries(allowedStyles).filter(
      ([styleItemId]) => !styleItemsFromPredefinedGroups.has(styleItemId),
    ),
  );

  const duplicateGroups = groupByDuplication(filteredAllowedStyles);

  return duplicateGroups.map((group) => {
    return {
      displayName: {
        group: null,
        component: getWidgetDisplayName(context),
      },
      editorPointer,
      items: group,
      styleNamespace: STYLE_NAMESPACE.CssCustomProperties,
      context,
    };
  });
};

const areAllItemsSiblings = (
  items: CssCustomPropertyGroupStyleItem[],
): boolean => {
  if (items.length <= 1) {
    return true;
  }

  const propertyTypes = items.map((item) => item.styleItem.cssPropertyType);
  const firstProperty = propertyTypes[0];

  if (!firstProperty) {
    return false;
  }

  const firstPropertySiblings = PROPERTY_SIBLINGS_MAP[firstProperty];
  if (!firstPropertySiblings) {
    return false;
  }

  return propertyTypes
    .slice(1)
    .every((property) => property && firstPropertySiblings.has(property));
};

const shouldGroupBeExcluedFromMerging = (
  items: CssCustomPropertyGroupStyleItem[],
) => {
  return checkForDuplicatedProperties(items) && !areAllItemsSiblings(items);
};

const checkForDuplicatedProperties = (
  items: CssCustomPropertyGroupStyleItem[],
) => {
  const properties = items.map((item) => item.styleItem.cssPropertyType);
  const uniqueProperties = [...new Set(properties)];
  return uniqueProperties.length !== properties.length;
};

const processDefinedGroups = (
  editorPointer: EditorPointer<CompRef>,
  allowedStyles: CssCustomProperties,
  context: VirtualManifestAPI,
): Omit<StylesGroup, 'engine'>[] => {
  if (!allowedStyles) {
    return [];
  }

  const groups: DisplayGroups =
    context.resolver.getDisplayGroupDefinitions() || {};

  const mappedGroups = Object.entries(groups)
    .map(([_, group]) => {
      const groupType = group.groupType;
      if (
        !groupType ||
        groupType === DISPLAY_GROUPS.GROUP_TYPE.UNKNOWN_GroupType
      ) {
        return null;
      }
      let accessKey: keyof DisplayGroupItem;
      // TODO remove if inconsistency in manifest is fixed
      if (groupType === DISPLAY_GROUPS.GROUP_TYPE.arrayItem) {
        accessKey = 'listItem';
      } else {
        // @ts-expect-error TBD fix
        accessKey = groupType;
      }
      const itemsGroup = group[accessKey];

      if (
        !itemsGroup ||
        // @ts-expect-error TBD fix
        !('items' in itemsGroup) ||
        !Array.isArray(itemsGroup.items)
      ) {
        return null;
      }

      const groupStyleItems: CssCustomPropertyGroupStyleItem[] = [];

      for (const styleItemId of itemsGroup.items) {
        const styleItem = allowedStyles?.[styleItemId];

        if (styleItem) {
          groupStyleItems.push({
            styleItemId,
            styleItem,
          });
        }
      }

      return {
        displayName: {
          group: group.displayName,
          component: getWidgetDisplayName(context),
        },
        editorPointer,
        styleNamespace: STYLE_NAMESPACE.CssCustomProperties,
        items: groupStyleItems,
        context,
        isExcluedFromMerging: shouldGroupBeExcluedFromMerging(groupStyleItems),
      };
    })
    .filter(
      (group) => group !== null && Boolean(group.items.length > 0),
    ) as StylesGroup[];

  return mappedGroups;
};

const processCssPropertyGroup = (
  editorPointer: EditorPointer<CompRef>,
  allowedStyles: CssProperties,
  context: VirtualManifestAPI,
): Omit<StylesGroup, 'engine'> => {
  return {
    displayName: {
      group: null,
      component: getWidgetDisplayName(context),
    },
    editorPointer,
    items: Object.entries(allowedStyles).map(([styleItemId, styleItem]) => ({
      styleItemId,
      styleItem,
    })),
    styleNamespace: STYLE_NAMESPACE.CssProperties,
    context,
  };
};

export const applyGrouping = (
  _actionName: StylesDerivedNativeActionNames,
  editorPointer: EditorPointer<CompRef>,
  allowedStyles: Styles,
  context: VirtualManifestAPI,
): Omit<StylesGroup, 'engine'>[] => {
  const resultingGroups: Omit<StylesGroup, 'engine'>[] = [];

  if (Object.keys(allowedStyles.cssCustomProperties).length > 0) {
    const predefinedGroups = processDefinedGroups(
      editorPointer,
      allowedStyles.cssCustomProperties,
      context,
    );
    const defaultGroups = processDefaultCustomPropertiesDuplicateGroups(
      editorPointer,
      allowedStyles.cssCustomProperties,
      predefinedGroups,
      context,
    );

    resultingGroups.push(...predefinedGroups, ...defaultGroups);
  }

  if (Object.keys(allowedStyles.cssProperties).length > 0) {
    const cssPropertyGroups = processCssPropertyGroup(
      editorPointer,
      allowedStyles.cssProperties,
      context,
    );
    resultingGroups.push(cssPropertyGroups);
  }

  return resultingGroups;
};
